import { Event, EventStatus, TennisGamePhase, TennisScore } from '../types/types';
import { EventDTO, TennisScoreDTO } from '../types/api';
import { sportOf } from '../utils/eventUtils';
import { apiService } from './api';
import { ENDPOINTS } from '../config/api';
import { featureService } from '../config/features';

// Helper function to create a minimal event object for corrupted data
const createCorruptedEvent = (dto: EventDTO): Event => {
    // Use whatever data is available from the DTO
    const currentTime = new Date().toISOString();
    
    return {
        id: dto.id,
        name: dto.name || 'Corrupted Event',
        provider: dto.provider || { id: 0, name: 'Unknown Provider' },
        providerEventId: dto.providerEventId || dto.id?.toString() || '0',
        sport: dto.sport || { id: 0, name: 'Unknown Sport' },
        competition: dto.competition ? {
            id: dto.competition.id,
            name: dto.competition.name,
            ruleSet: dto.competition.ruleSet || 'unknown',
            marginLevel: dto.competition.marginLevel ?? null
        } : { id: 0, name: 'Unknown Competition', ruleSet: 'unknown', marginLevel: null },
        providerName: dto.providerName || 'Unknown',
        videoUrl: dto.videoUrl || null,
        streamId: dto.streamId,
        startTime: dto.startTime || currentTime,
        firstCompetitor: dto.firstCompetitor || { id: 0, name: 'Unknown' },
        secondCompetitor: dto.secondCompetitor || { id: 0, name: 'Unknown' },
        status: EventStatus.DISABLED_BY_SYSTEM,
        providerEventStatus: EventStatus.DISABLED_BY_SYSTEM,
        dataVersion: 0,
        sourceTimestamp: Date.now(),
        createdAt: dto.createdAt || currentTime,
        updatedAt: dto.updatedAt || currentTime,
        isCorrupted: true // Mark as corrupted
    };
};

const MS_PER_SECOND = 1000;

// Helper function to convert API DTO to frontend Event type
const toTennisScore = (dto: TennisScoreDTO): TennisScore => ({
    sequence: dto.sequence,
    timestamp: dto.timestamp * MS_PER_SECOND,
    gamePhase: dto.gamePhase as TennisGamePhase,
    currentSet: dto.currentSet,
    currentGame: dto.currentGame,
    serverCompetitorId: dto.serverCompetitorId ?? undefined,
    pointWinnerCompetitorId: dto.pointWinnerCompetitorId ?? undefined,
    players: dto.players ?? [],
    completedSets: (dto.completedSets ?? []).map(set => ({
        set: set.set,
        winnerCompetitorId: set.winnerCompetitorId ?? undefined,
        scores: set.scores ?? []
    }))
});

const convertDTOtoEvent = (dto: EventDTO): Event => {
    // Extract startTime from DTO if available, otherwise use current time
    const startTime = dto.startTime ? dto.startTime : new Date().toISOString();
    
    // Extract status from DTO if available, otherwise use ACTIVE as default
    const status = dto.status ? 
        (typeof dto.status === 'string' ? EventStatus[dto.status as keyof typeof EventStatus] : dto.status) : 
        EventStatus.ACTIVE;
    
    // If any required objects are missing, throw an error
    if (!dto.provider || !dto.sport || !dto.competition || !dto.firstCompetitor || !dto.secondCompetitor) {
        console.error('Missing required data from backend:', dto);
        throw new Error('Event data from backend is incomplete');
    }
    
    // Debug the cricket scores from the API
    console.group('Cricket Scores Debug - API Response');
    console.log('DTO has cricketScores:', Boolean(dto.cricketScores));
    if (dto.cricketScores) {
        console.log('Number of cricket scores in DTO:', dto.cricketScores.length);
        if (dto.cricketScores.length > 0) {
            console.log('First cricket score in DTO:', dto.cricketScores[0]);
        }
    }
    console.groupEnd();
    
    // Convert cricket scores if they exist
    const cricketScores = dto.cricketScores ? dto.cricketScores.map(scoreDto => {
        // Log any missing properties
        const missingProps = [];
        if (scoreDto.sequenceId === undefined) missingProps.push('sequenceId');
        if (scoreDto.timestamp === undefined) missingProps.push('timestamp');
        if (scoreDto.ballReference === undefined) missingProps.push('ballReference');
        if (scoreDto.runs === undefined) missingProps.push('runs');
        if (scoreDto.isWicket === undefined) missingProps.push('isWicket');
        if (scoreDto.firstTeamScore === undefined) missingProps.push('firstTeamScore');
        if (scoreDto.secondTeamScore === undefined) missingProps.push('secondTeamScore');
        
        if (missingProps.length > 0) {
            console.warn('Missing cricket score properties:', missingProps, scoreDto);
        }
        
        return {
            sequenceId: scoreDto.sequenceId,
            timestamp: scoreDto.timestamp,
            ballReference: scoreDto.ballReference,
            runs: scoreDto.runs,
            isWicket: scoreDto.isWicket,
            firstTeamScore: scoreDto.firstTeamScore,
            secondTeamScore: scoreDto.secondTeamScore
        };
    }) : undefined;
    
    // Debug the mapped cricket scores
    if (cricketScores) {
        console.log('Mapped cricket scores:', cricketScores.length, 'scores');
    }

    // Table tennis scores mirror the DTO shape one-to-one
    const ttScores = dto.ttScores ? dto.ttScores.map(scoreDto => ({
        sequenceId: scoreDto.sequenceId,
        timestamp: scoreDto.timestamp,
        currentSet: scoreDto.currentSet,
        serverCompetitorId: scoreDto.serverCompetitorId,
        pointWinnerCompetitorId: scoreDto.pointWinnerCompetitorId,
        players: scoreDto.players
    })) : undefined;

    const tennisScores = dto.sport && sportOf({ sport: dto.sport }) === 'TENNIS' && dto.scores ? dto.scores.map(toTennisScore) : undefined;

    return {
        id: dto.id,
        name: dto.name,
        provider: dto.provider,
        providerEventId: dto.providerEventId || dto.id.toString(),
        sport: dto.sport,
        competition: {
            id: dto.competition.id,
            name: dto.competition.name,
            ruleSet: dto.competition.ruleSet || 'default',
            marginLevel: dto.competition.marginLevel ?? null
        },
        providerName: dto.providerName || dto.name,
        videoUrl: dto.videoUrl || null,
        streamId: dto.streamId,
        startTime: startTime,
        firstCompetitor: dto.firstCompetitor,
        secondCompetitor: dto.secondCompetitor,
        status: status,
        providerEventStatus: status,
        dataVersion: 1,
        sourceTimestamp: Date.now(),
        createdAt: dto.createdAt || new Date().toISOString(),
        updatedAt: dto.updatedAt || new Date().toISOString(),
        cricketScores: cricketScores,
        ttScores: ttScores,
        tennisScores: tennisScores
    };
};

export const eventService = {
    getAllEvents: async (startTime?: Date): Promise<Event[]> => {
        try {
            let url = ENDPOINTS.EVENTS;
            if (startTime) {
                // Convert Date to ISO string and encode for URL
                const startTimeParam = encodeURIComponent(startTime.toISOString());
                url = `${url}?startTime=${startTimeParam}`;
            }
            const dtos = await apiService.get<EventDTO[]>(url);
            
            // Process all DTOs, marking incomplete ones as corrupted
            return dtos.map(dto => {
                try {
                    // Check if the DTO has all required fields
                    const isCorrupted = !dto.provider || !dto.sport || !dto.competition || 
                                      !dto.firstCompetitor || !dto.secondCompetitor;
                    
                    if (isCorrupted) {
                        console.warn('Found corrupted event data:', dto);
                        // Create a minimal event with corrupted flag
                        return createCorruptedEvent(dto);
                    }
                    
                    // Regular conversion for complete DTOs
                    return convertDTOtoEvent(dto);
                } catch (conversionError) {
                    console.error('Error converting event DTO:', conversionError, dto);
                    // Create a minimal event with corrupted flag for conversion errors
                    return createCorruptedEvent(dto);
                }
            });
        } catch (error) {
            console.error('Error fetching events:', error);
            return [];
        }
    },

    getEventById: async (id: number): Promise<Event | null> => {
        try {
            const dto = await apiService.get<EventDTO>(`${ENDPOINTS.EVENTS}/${id}`);
            return convertDTOtoEvent(dto);
        } catch (error) {
            if ((error as any).status === 404) {
                return null;
            }
            throw error;
        }
    },

    // Note: These methods might need to be implemented on the backend first
    updateEvent: async (id: number, updateData: Partial<Event>): Promise<Event> => {
        const updatedEvent = await apiService.put<EventDTO>(`${ENDPOINTS.EVENTS}/${id}`, updateData);
        return convertDTOtoEvent(updatedEvent);
    },

    cancelEvent: async (id: number): Promise<Event> => {
        return eventService.updateEvent(id, {
            status: EventStatus.DISABLED_BY_SYSTEM,
            providerEventStatus: EventStatus.DISABLED_BY_SYSTEM
        });
    }
};
